<template>
  <div class="low-stock">
    <!-- Filters + actions -->
    <div class="rp-toolbar">
      <div class="rp-filters" style="--rp-cols: 3">
        <div class="rp-filter">
          <label>Warehouse</label>
          <FormDropdown v-model="filters.warehouse" :options="warehouseOptions" placeholder="All warehouses" clearable />
        </div>
        <div class="rp-filter">
          <label>Category</label>
          <FormDropdown v-model="filters.category" :options="categoryOptions" placeholder="All categories" clearable />
        </div>
        <div class="rp-filter">
          <label>Subcategory</label>
          <FormDropdown v-model="filters.sub_category" :options="subCategoryOptions" placeholder="All subcategories" clearable />
        </div>
        <div class="rp-filter rp-search">
          <label>Item</label>
          <div class="position-relative">
            <i class="ti ti-search rp-search-icon"></i>
            <input v-model="filters.search" type="text" class="form-control" placeholder="Search name, code, SKU or barcode" />
          </div>
        </div>
      </div>

      <ReportActions
        :loading="loading"
        :exporting="exporting"
        :can-reset="itemFiltersActive || statusFilter !== 'all'"
        :can-print="!!rows.length"
        @reset="resetFilters"
        @export="exportFile"
        @print="printReport"
      />
    </div>

    <div v-if="error" class="alert alert-danger d-flex align-items-center gap-2">
      <i class="ti ti-alert-circle"></i>{{ error }}
    </div>

    <!-- Summary cards -->
    <div class="rp-cards" :class="{ 'is-loading': loading }">
      <button type="button" class="rp-card rp-card-action tone-red" :class="{ selected: statusFilter === 'out_of_stock' }"
              @click="toggleStatus('out_of_stock')">
        <span class="rp-card-icon"><i class="ti ti-package-off"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Out of Stock</div>
          <div class="rp-card-value text-danger">{{ formatInt(summary.out_count) }}</div>
          <div class="rp-card-sub">items with nothing to sell</div>
        </div>
      </button>
      <button type="button" class="rp-card rp-card-action tone-orange" :class="{ selected: statusFilter === 'low_stock' }"
              @click="toggleStatus('low_stock')">
        <span class="rp-card-icon"><i class="ti ti-alert-triangle"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Low Stock</div>
          <div class="rp-card-value" style="color: #D9771E">{{ formatInt(summary.low_count) }}</div>
          <div class="rp-card-sub">at or below the reorder level</div>
        </div>
      </button>
      <div class="rp-card tone-navy">
        <span class="rp-card-icon"><i class="ti ti-arrow-down-bar"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Shortage</div>
          <div class="rp-card-value">{{ formatQty(summary.shortage_qty) }}</div>
          <div class="rp-card-sub">units below reorder levels</div>
        </div>
      </div>
      <div class="rp-card tone-blue">
        <span class="rp-card-icon"><i class="ti ti-shopping-cart-plus"></i></span>
        <div class="min-w-0">
          <div class="rp-card-label">Suggested Order</div>
          <div class="rp-card-value">{{ formatQty(summary.suggested_qty) }}</div>
          <div class="rp-card-sub">≈ {{ formatMoney(summary.reorder_value) }} at last cost</div>
        </div>
      </div>
    </div>

    <!-- Insights -->
    <div class="rp-insights" :class="{ 'is-loading': loading }">
      <div class="rp-panel">
        <div class="rp-panel-title">Stock alert split</div>
        <div class="rp-bars">
          <div v-for="bar in statusBars" :key="bar.key">
            <div class="d-flex justify-content-between small mb-1">
              <span>{{ bar.label }}</span>
              <span class="fw-semibold">{{ formatInt(bar.count) }} <span class="text-muted fw-normal">({{ bar.share }}%)</span></span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :class="bar.tone" :style="{ width: `${bar.share}%` }"></div></div>
          </div>
        </div>
        <div class="rp-note mt-3">
          <strong>{{ formatInt(summary.selling_items) }}</strong> of these sold in the last {{ summary.sales_days }} days —
          reorder those first.
        </div>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">By category</div>
        <div v-if="categoryBars.length" class="rp-bars">
          <div v-for="category in categoryBars" :key="category.name">
            <div class="d-flex justify-content-between small mb-1">
              <span class="text-truncate me-2">{{ category.name }}</span>
              <span>
                <span v-if="category.out" class="rp-chip out me-1">{{ category.out }} out</span>
                <span v-if="category.low" class="rp-badge warning">{{ category.low }} low</span>
              </span>
            </div>
            <div class="rp-bar-track"><div class="rp-bar-fill" :style="{ width: `${category.percent}%` }"></div></div>
          </div>
        </div>
        <p v-else class="text-muted small mb-0">No low stock items.</p>
      </div>

      <div class="rp-panel">
        <div class="rp-panel-title">Most urgent</div>
        <ul v-if="summary.urgent.length" class="rp-ranked">
          <li v-for="(item, index) in summary.urgent" :key="item.name">
            <span class="rp-rank">{{ index + 1 }}</span>
            <div class="flex-grow-1 min-w-0">
              <div class="text-truncate" :title="item.name">{{ item.name }}</div>
              <small class="text-muted">{{ formatQty(item.available_qty) }} left · sold {{ formatQty(item.sold_qty) }}</small>
            </div>
            <span class="rp-badge" :class="item.status === 'out_of_stock' ? 'danger' : 'warning'">
              {{ daysLeftText(item) }}
            </span>
          </li>
        </ul>
        <p v-else class="text-muted small mb-0">Nothing needs reordering.</p>
      </div>
    </div>

    <!-- Table -->
    <div class="rp-table-card">
      <div class="rp-table-head">
        <div class="rp-pills">
          <button v-for="pill in statusPills" :key="pill.key" type="button" class="rp-pill"
                  :class="{ active: statusFilter === pill.key }" @click="statusFilter = pill.key">
            {{ pill.label }} <span class="rp-pill-count">{{ formatInt(pill.count) }}</span>
          </button>
        </div>
        <div class="d-flex align-items-center gap-2 small text-muted">
          Show
          <select v-model.number="pageSize" class="form-select form-select-sm rp-page-size">
            <option v-for="size in PAGE_SIZES" :key="size" :value="size">{{ size }}</option>
          </select>
          per page
        </div>
      </div>

      <div class="table-responsive rp-table-wrap">
        <div v-if="loading" class="rp-loading"><span class="spinner-border text-warning"></span></div>
        <table class="table rp-table mb-0">
          <thead>
            <tr>
              <th v-for="column in columns" :key="column.key" :class="column.class" :title="column.title" @click="sortBy(column.key)">
                <span class="rp-sortable" :class="{ active: sortKey === column.key }">
                  {{ column.label }}
                  <i class="ti" :class="sortIcon(column.key)"></i>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in pagedRows" :key="row.key">
              <td class="fw-semibold">{{ row.item_code }}</td>
              <td>
                <div class="fw-medium">{{ row.item_name }}</div>
                <span v-if="row.supplier || row.last_received" class="rp-sub">
                  <template v-if="row.supplier">{{ row.supplier }}</template>
                  <template v-if="row.supplier && row.last_received"> · </template>
                  <template v-if="row.last_received">last received {{ formatDate(row.last_received) }}</template>
                </span>
              </td>
              <td>
                <template v-if="row.variant">
                  <span v-for="part in row.variant.split(' · ')" :key="part" class="rp-variant">{{ part }}</span>
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td>{{ row.warehouse }}</td>
              <td class="text-end fw-semibold" :class="{ 'text-danger': Number(row.available_qty) <= 0 }">
                {{ formatQty(row.available_qty) }}<small v-if="row.unit" class="text-muted ms-1">{{ row.unit }}</small>
              </td>
              <td class="text-end">{{ row.reorder_level ? formatQty(row.reorder_level) : '—' }}</td>
              <td class="text-end">{{ Number(row.shortage) ? formatQty(row.shortage) : '—' }}</td>
              <td class="text-end">{{ Number(row.sold_qty) ? formatQty(row.sold_qty) : '—' }}</td>
              <td class="text-end">
                <span class="rp-badge" :class="row.status === 'out_of_stock' ? 'danger' : row.days_left !== null && row.days_left <= 7 ? 'warning' : 'muted'">
                  {{ daysLeftText(row) }}
                </span>
              </td>
              <td class="text-end fw-semibold">{{ Number(row.suggested_qty) ? formatQty(row.suggested_qty) : '—' }}</td>
              <td class="text-end">{{ Number(row.reorder_value) ? formatMoney(row.reorder_value) : '—' }}</td>
              <td>
                <span class="rp-badge" :class="row.status === 'out_of_stock' ? 'danger' : 'warning'">
                  {{ row.status === 'out_of_stock' ? 'Out of stock' : 'Low stock' }}
                </span>
              </td>
            </tr>

            <tr v-if="!loading && !pagedRows.length">
              <td :colspan="columns.length">
                <div class="rp-empty">
                  <i class="ti ti-circle-check text-success"></i>
                  <div class="fw-semibold">{{ rows.length ? 'No items in this view' : 'All items are above their reorder level' }}</div>
                  <small class="text-muted">Items show here when they run out or drop to their reorder level.</small>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="visibleRows.length">
            <tr>
              <th colspan="6" class="text-end">Total ({{ plural(visibleRows.length, 'item') }})</th>
              <th class="text-end">{{ formatQty(visibleTotals.shortage) }}</th>
              <th colspan="2"></th>
              <th class="text-end">{{ formatQty(visibleTotals.suggested_qty) }}</th>
              <th class="text-end">{{ formatMoney(visibleTotals.reorder_value) }}</th>
              <th></th>
            </tr>
          </tfoot>
        </table>
      </div>

      <ReportPagination
        v-if="visibleRows.length"
        :page="page"
        :page-count="pageCount"
        :pages="pageNumbers"
        :start="pageStart"
        :end="pageEnd"
        :total="visibleRows.length"
        @go="goToPage"
      />
    </div>
    <p class="rp-hint mt-2 mb-0">
      Suggested order = enough to get back to the reorder level and cover the next {{ summary.sales_days }} days of sales
      at the last {{ summary.sales_days }} days' rate.
    </p>
  </div>
</template>

<script>
import apiUrls from '@/urls';
import inventoryReport, { escapeHtml, localToday } from './inventoryReportMixin';

const EMPTY_SUMMARY = {
  out_count: 0, low_count: 0, shortage_qty: 0, suggested_qty: 0, reorder_value: 0, selling_items: 0,
  sales_days: 30, by_category: [], urgent: [],
};

export default {
  name: 'LowStockReport',
  mixins: [inventoryReport],

  reportUrl: apiUrls.low_stock_report.get,
  fileName: 'low_stock',
  columns: [
    { key: 'item_code', label: 'Item Code' },
    { key: 'item_name', label: 'Item Name' },
    { key: 'variant', label: 'Variant' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'available_qty', label: 'Available', class: 'text-end', numeric: true },
    { key: 'reorder_level', label: 'Reorder Lvl', class: 'text-end', numeric: true },
    { key: 'shortage', label: 'Shortage', class: 'text-end', numeric: true },
    { key: 'sold_qty', label: 'Sold 30d', class: 'text-end', numeric: true, title: 'Net quantity sold in the last 30 days' },
    { key: 'days_left', label: 'Days Left', class: 'text-end', numeric: true, title: 'Days until it runs out at the recent sales rate' },
    { key: 'suggested_qty', label: 'Suggested', class: 'text-end', numeric: true },
    { key: 'reorder_value', label: 'Order Value', class: 'text-end', numeric: true },
    { key: 'status', label: 'Status' },
  ],

  data() {
    return {
      filters: { warehouse: null, category: null, sub_category: null, search: '' },
      summary: { ...EMPTY_SUMMARY },
      statusFilter: 'all',
      sortKey: null,
      sortDir: 'asc',
    };
  },

  computed: {
    statusPills() {
      return [
        { key: 'all', label: 'All alerts', count: this.rows.length },
        { key: 'out_of_stock', label: 'Out of stock', count: this.summary.out_count },
        { key: 'low_stock', label: 'Low stock', count: this.summary.low_count },
      ];
    },

    statusBars() {
      const total = this.rows.length || 1;
      return [
        { key: 'out', label: 'Out of stock', count: this.summary.out_count, tone: 'danger' },
        { key: 'low', label: 'Low stock', count: this.summary.low_count, tone: 'warning' },
      ].map((bar) => ({ ...bar, share: this.rows.length ? Math.round((bar.count / total) * 100) : 0 }));
    },

    categoryBars() {
      return this.withPercent(this.summary.by_category.map((c) => ({ ...c, total: c.out + c.low })), 'total');
    },

    visibleRows() {
      const rows = this.statusFilter === 'all' ? this.rows : this.rows.filter((r) => r.status === this.statusFilter);
      // Without a chosen column the server's urgency order is kept.
      return this.sortKey ? this.sortRows(rows) : rows;
    },

    visibleTotals() {
      const fields = ['shortage', 'suggested_qty', 'reorder_value'];
      return this.visibleRows.reduce((totals, row) => {
        fields.forEach((field) => { totals[field] += Number(row[field]) || 0; });
        return totals;
      }, Object.fromEntries(fields.map((field) => [field, 0])));
    },
  },

  watch: {
    statusFilter() { this.page = 1; },
  },

  methods: {
    extraParams() {
      return {};
    },

    resetExtraFilters() {
      this.statusFilter = 'all';
      this.sortKey = null;
      return {};
    },

    applyResult(data) {
      this.summary = { ...EMPTY_SUMMARY, ...(data?.summary || {}) };
    },

    toggleStatus(status) {
      this.statusFilter = this.statusFilter === status ? 'all' : status;
    },

    daysLeftText(row) {
      if (row.status === 'out_of_stock' || Number(row.available_qty) <= 0) return 'Out now';
      if (row.days_left === null || row.days_left === undefined) return 'No recent sales';
      return row.days_left === 0 ? 'Today' : `${this.formatInt(row.days_left)} days`;
    },

    printReport() {
      const s = this.summary;
      const body = this.visibleRows.map((r) => `
        <tr>
          <td>${escapeHtml(r.item_code)}</td>
          <td>${escapeHtml(r.item_name)}${r.supplier ? `<br><small>${escapeHtml(r.supplier)}</small>` : ''}</td>
          <td>${escapeHtml(r.variant)}</td>
          <td>${escapeHtml(r.warehouse)}</td>
          <td class="num">${this.formatQty(r.available_qty)}</td>
          <td class="num">${this.formatQty(r.reorder_level)}</td>
          <td class="num">${this.formatQty(r.shortage)}</td>
          <td class="num">${this.formatQty(r.sold_qty)}</td>
          <td class="num">${escapeHtml(this.daysLeftText(r))}</td>
          <td class="num">${this.formatQty(r.suggested_qty)}</td>
          <td class="num">${this.formatMoney(r.reorder_value)}</td>
          <td class="${r.status}">${r.status === 'out_of_stock' ? 'Out of stock' : 'Low stock'}</td>
        </tr>`).join('');
      const t = this.visibleTotals;
      const table = `<table><thead><tr><th>Item Code</th><th>Item Name</th><th>Variant</th><th>Warehouse</th>
          <th class="num">Available</th><th class="num">Reorder Lvl</th><th class="num">Shortage</th>
          <th class="num">Sold 30d</th><th class="num">Days Left</th><th class="num">Suggested</th>
          <th class="num">Order Value</th><th>Status</th></tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr><td colspan="6" class="num">Total</td><td class="num">${this.formatQty(t.shortage)}</td>
          <td colspan="2"></td><td class="num">${this.formatQty(t.suggested_qty)}</td>
          <td class="num">${this.formatMoney(t.reorder_value)}</td><td></td></tr></tfoot></table>`;
      this.openPrint(
        'Low Stock',
        [[`As on: ${this.formatDate(localToday())}`, ...this.itemFiltersText()].join(' · '),
          `Printed ${new Date().toLocaleString()}`],
        [
          ['Out of Stock', this.plural(s.out_count, 'item')],
          ['Low Stock', this.plural(s.low_count, 'item')],
          ['Shortage', this.formatQty(s.shortage_qty)],
          ['Suggested Order', `${this.formatQty(s.suggested_qty)} · ${this.formatMoney(s.reorder_value)}`],
        ],
        table,
      );
    },
  },
};
</script>

<style scoped src="./inventoryReport.css"></style>
